import React, { ComponentType, useId, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import Select from "../../../shared/Select/Select";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import { getAdminNow } from "../../../../utils/dateService";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { ChartCard } from "../../../shared/Charts";
import GeographicUserInsightsJs from "../../../shared/Dashboard/GeographicUserInsights/GeographicUserInsights";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import { type CasinoByStateMapRow } from "../casinoKpisV2MockData";
import {
  useGetCasinoByStateV2,
  type CasinoStateV2Row,
} from "../../../../queries/casino-kpis/useGetCasinoByStateV2";
import type { CasinoKpiFilterOption } from "../../../../queries/casino-kpis/casinoKpiFilters";

import styles from "./CasinoByStateV2.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;
const GeographicUserInsights = GeographicUserInsightsJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

type MetricKey = Exclude<keyof CasinoStateV2Row, "name" | "abbr">;

const CURRENCY_METRICS = new Set<MetricKey>(["turnover", "ggr"]);

// `DatePicker.getData` is required but the section re-fetches reactively via
// `dateFilters` -> queryKey. Hoisting the no-op to module scope makes the
// intent obvious and avoids a needless callback per render.
const NOOP_GET_DATA = (_queryString: string | null): void => undefined;

export type CasinoByStateV2Props = {
  /**
   * Optional row override. Used by tests / Storybook to render a deterministic
   * dataset; production wires the live API hook. The legacy mock-data prop
   * shape (`turnover` / `ggr` / `total_rounds` keyed by state) is supported
   * via `CasinoByStateMapRow`, but live data flows through `CasinoStateRow`
   * which is structurally identical for the metrics this component renders.
   */
  rows?: CasinoByStateMapRow[];
  country?: string;
  moneyTypeOptions?: CasinoKpiFilterOption[];
};

const CasinoByStateV2: React.FC<CasinoByStateV2Props> = ({
  rows: rowsProp,
  country = "USA",
  moneyTypeOptions = [],
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [metric, setMetric] = useState<MetricKey>("turnover");
  const [moneyTypes, setMoneyTypes] = useState<string[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filtersId = useId();

  const currencySymbol = useCurrencySymbol() ?? "$";

  // Defensive `?.format() ?? ""` guards against a future change to the date
  // picker (e.g. enabling `allowClearToNull`) handing us a missing endpoint
  // — the hook treats empty strings as "disabled" and skips the request.
  const dateFilters = useMemo(
    () => ({
      start_date: dates[0]?.format("YYYY-MM-DD") ?? "",
      end_date: dates[1]?.format("YYYY-MM-DD") ?? "",
      money_type: moneyTypes,
    }),
    [dates, moneyTypes],
  );

  const {
    data: liveRows = [],
    isFetching,
    isLoading,
    isError,
  } = useGetCasinoByStateV2(dateFilters);

  // The `rows` prop wins when supplied (tests / storybook); otherwise we
  // render the live API rows.
  const rows = rowsProp ?? liveRows;

  const showLoading = rowsProp === undefined && (isLoading || isFetching);
  const showEmpty =
    !showLoading && !isError && rowsProp === undefined && rows.length === 0;

  const metricOptions: { value: MetricKey; label: string }[] = [
    { value: "turnover", label: AppStrings.TURNOVER },
    { value: "ggr", label: AppStrings.GGR },
    { value: "total_rounds", label: AppStrings.TOTAL_ROUNDS },
  ];

  const mapData = useMemo(
    () =>
      rows.map((s) => ({
        name: s.name,
        abbr: s.abbr,
        amount: s[metric],
      })),
    [rows, metric],
  );

  const exportRows = useMemo(
    () =>
      rows.map((s) => ({
        state: s.name,
        abbr: s.abbr,
        turnover: s.turnover,
        ggr: s.ggr,
        total_rounds: s.total_rounds,
      })),
    [rows],
  );

  return (
    <ChartCard
      title={AppStrings.CASINO_BY_STATE}
      headerExtra={
        <>
          <span className={styles.filterToggle}>
            <Button
              onClick={() => setFiltersVisible((visible) => !visible)}
              aria-expanded={filtersVisible}
              aria-controls={filtersId}
              aria-label={AppStrings.PAM_REPORT_FILTERS}
            >
              <Filter size={18} aria-hidden="true" />
              {filtersVisible ? (
                <ChevronUp size={16} aria-hidden="true" />
              ) : (
                <ChevronDown size={16} aria-hidden="true" />
              )}
            </Button>
          </span>
          <div
            id={filtersId}
            className={`${styles.headerSelects}${
              filtersVisible ? "" : ` ${styles.filtersCollapsed}`
            }`}
            data-testid="casino-by-state-filters"
          >
            <Select
              className={styles.metricSelect}
              value={metric}
              options={metricOptions}
              onChange={(value) => setMetric(value as MetricKey)}
              aria-label={AppStrings.METRIC}
            />
            <Select
              className={styles.moneyTypeSelect}
              mode="multiple"
              value={moneyTypes}
              options={moneyTypeOptions}
              onChange={(value) => setMoneyTypes(value as string[])}
              aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
              placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
              maxTagCount="responsive"
              showSearch
              optionFilterProp="label"
            />
          </div>
        </>
      }
      controls={
        <>
          <CustomDatePicker
            getData={NOOP_GET_DATA}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="casino-by-state.xlsx"
          />
        </>
      }
    >
      {isError && rowsProp === undefined ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="casino-by-state-error"
        >
          {AppStrings.CASINO_BY_STATE_LOAD_ERROR}
        </p>
      ) : showLoading ? (
        <div className={styles.loaderWrapper} data-testid="casino-by-state-loader">
          <RebetLoader width={100} height={100} />
        </div>
      ) : showEmpty ? (
        <p
          className={styles.emptyMessage}
          data-testid="casino-by-state-empty"
        >
          {AppStrings.CASINO_BY_STATE_EMPTY_STATE}
        </p>
      ) : (
        <div className={styles.mapContainer}>
          <GeographicUserInsights
            mapId="casino-by-state-v2"
            data={mapData}
            isDollars={CURRENCY_METRICS.has(metric)}
            country={country}
            currencySymbol={currencySymbol}
          />
        </div>
      )}
    </ChartCard>
  );
};

export default CasinoByStateV2;
